import React, { useState, useEffect } from 'react';
import { 
  Save, FolderOpen, Trash2, Share2, Lock, User, Building2,
  ChevronDown, Check, X, Plus, AlertCircle, ArrowUpCircle
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

/**
 * PresetManager - Save, load, and manage filter presets
 * Supports both personal and org-shared presets with tier gating
 */
const PresetManager = ({
  pageType = 'epics', // 'epics', 'features', 'stories', 'tasks', 'issues', 'dependencies', 'decisions'
  currentFilters = {},
  currentQuickFilters = {},
  onLoadPreset,
  className = '',
}) => {
  const { user, tenant } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [presets, setPresets] = useState({ personal: [], shared: [] });
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetShared, setNewPresetShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const userId = user?.id || user?._id;
  const tenantId = tenant?.id || user?.tenant?.id || user?.tenant_id;
  const userName = user?.name || user?.email || 'User';

  useEffect(() => {
    if (isOpen && userId && tenantId) {
      loadPresets();
      loadTierInfo();
    }
  }, [isOpen, userId, tenantId, pageType]);

  const loadPresets = async () => {
    try {
      setLoading(true);
      const res = await api.getUserPresets(userId, tenantId, pageType);
      setPresets({
        personal: res.data.personal || [],
        shared: res.data.shared || []
      });
    } catch (err) {
      console.error('Failed to load presets:', err);
      setError('Failed to load presets');
    } finally {
      setLoading(false);
    }
  };

  const loadTierInfo = async () => {
    try {
      const res = await api.getPresetTierInfo(tenantId, userId);
      setTierInfo(res.data);
    } catch (err) {
      console.error('Failed to load tier info:', err);
    }
  };

  const handleSavePreset = async () => {
    if (!newPresetName.trim()) {
      setError('Please enter a preset name');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const preset = {
        name: newPresetName.trim(),
        page_type: pageType,
        filters: currentFilters,
        quick_filters: currentQuickFilters,
        is_shared: newPresetShared
      };

      await api.createUserPreset(preset, userId, userName, tenantId);
      
      setShowSaveModal(false);
      setNewPresetName('');
      setNewPresetShared(false);
      await loadPresets();
      await loadTierInfo();
    } catch (err) {
      console.error('Failed to save preset:', err);
      setError(err.response?.data?.detail || 'Failed to save preset');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadPreset = (preset) => {
    onLoadPreset({
      filters: preset.filters || {},
      quickFilters: preset.quick_filters || {}
    });
    setIsOpen(false);
  };

  const handleDeletePreset = async (presetId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this preset?')) {
      return;
    }

    try {
      await api.deleteUserPreset(presetId, userId);
      await loadPresets();
      await loadTierInfo();
    } catch (err) {
      console.error('Failed to delete preset:', err);
      setError(err.response?.data?.detail || 'Failed to delete preset');
    }
  };

  const canCreatePersonal = tierInfo?.can_create_personal !== false;
  const canCreateShared = tierInfo?.can_create_shared === true;
  const sharedEnabled = tierInfo?.shared_enabled === true;

  const allPresets = [...presets.personal, ...presets.shared];

  return (
    <div className={`relative ${className}`}>
      {/* Dropdown Toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium text-[var(--clr-text-secondary)] transition-colors"
        data-testid="preset-manager-toggle"
      >
        <FolderOpen className="w-4 h-4" />
        <span>Presets</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-testid="preset-dropdown">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-[var(--clr-text-primary)]">Filter Presets</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            
            {/* Tier Info */}
            {tierInfo && (
              <div className="mt-2 text-xs text-[var(--clr-text-muted)]">
                <span className="capitalize">{tierInfo.tier}</span> tier: 
                <span className="ml-1">{tierInfo.counts?.personal || 0}/{tierInfo.limits?.personal}</span> personal
                {sharedEnabled && (
                  <span className="ml-2">{tierInfo.counts?.shared || 0}/{tierInfo.limits?.shared}</span>
                )}
                {sharedEnabled && ' shared'}
              </div>
            )}
          </div>

          {/* Preset List */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-sm text-gray-400">Loading...</div>
            ) : allPresets.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-400">
                No presets saved yet
              </div>
            ) : (
              <div className="py-2">
                {/* Personal Presets */}
                {presets.personal.length > 0 && (
                  <div>
                    <div className="px-4 py-1 text-xs font-semibold text-[var(--clr-text-muted)] uppercase flex items-center gap-1">
                      <User className="w-3 h-3" /> Personal
                    </div>
                    {presets.personal.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => handleLoadPreset(preset)}
                        className="w-full px-4 py-2 text-left hover:bg-[var(--clr-surface-hover)] flex items-center justify-between group"
                        data-testid={`preset-${preset.id}`}
                      >
                        <span className="text-sm text-[var(--clr-text-primary)]">{preset.name}</span>
                        <button
                          onClick={(e) => handleDeletePreset(preset.id, e)}
                          className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Shared Presets */}
                {presets.shared.length > 0 && (
                  <div className="mt-2">
                    <div className="px-4 py-1 text-xs font-semibold text-[var(--clr-text-muted)] uppercase flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> Shared
                    </div>
                    {presets.shared.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => handleLoadPreset(preset)}
                        className="w-full px-4 py-2 text-left hover:bg-[var(--clr-surface-hover)] flex items-center justify-between group"
                        data-testid={`preset-shared-${preset.id}`}
                      >
                        <div>
                          <span className="text-sm text-[var(--clr-text-primary)]">{preset.name}</span>
                          <span className="ml-2 text-xs text-[var(--clr-text-muted)]">
                            by {preset.created_by_name}
                          </span>
                        </div>
                        {preset.created_by === userId && (
                          <button
                            onClick={(e) => handleDeletePreset(preset.id, e)}
                            className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-4 py-3 border-t border-gray-100 space-y-2">
            {/* Save Current Filters */}
            {canCreatePersonal || canCreateShared ? (
              <button
                onClick={() => setShowSaveModal(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm font-medium hover:opacity-90"
                data-testid="save-preset-btn"
              >
                <Save className="w-4 h-4" />
                Save Current Filters
              </button>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <AlertCircle className="w-4 h-4" />
                <span>Preset limit reached</span>
              </div>
            )}

            {/* Upgrade Prompt */}
            {tierInfo?.upgrade_prompt && (
              <button className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg text-sm font-medium hover:opacity-90">
                <ArrowUpCircle className="w-4 h-4" />
                Upgrade for More Presets
              </button>
            )}
          </div>
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6" data-testid="save-preset-modal">
            <h3 className="text-lg font-semibold mb-4">Save Filter Preset</h3>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                  Preset Name
                </label>
                <input
                  type="text"
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  placeholder="e.g., High Priority Blocked Items"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                  data-testid="preset-name-input"
                />
              </div>

              {sharedEnabled && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setNewPresetShared(false)}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${
                      !newPresetShared 
                        ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] bg-opacity-10 text-[var(--clr-brand-primary)]' 
                        : 'border-gray-200 text-[var(--clr-text-secondary)]'
                    }`}
                  >
                    <User className="w-4 h-4" />
                    Personal
                  </button>
                  <button
                    onClick={() => setNewPresetShared(true)}
                    disabled={!canCreateShared}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${
                      newPresetShared 
                        ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] bg-opacity-10 text-[var(--clr-brand-primary)]' 
                        : 'border-gray-200 text-[var(--clr-text-secondary)]'
                    } ${!canCreateShared ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Share2 className="w-4 h-4" />
                    Shared
                    {!canCreateShared && <Lock className="w-3 h-3" />}
                  </button>
                </div>
              )}

              {!sharedEnabled && (
                <div className="text-xs text-[var(--clr-text-muted)] flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Shared presets available on Business+ tiers
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setNewPresetName('');
                  setNewPresetShared(false);
                  setError(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-[var(--clr-text-secondary)] hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePreset}
                disabled={saving || !newPresetName.trim()}
                className="flex-1 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-save-preset"
              >
                {saving ? 'Saving...' : 'Save Preset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresetManager;
